/**
 * CalendarActionDialogs — the ONE renderer for the calendar's action-dialog
 * union (CalendarStore.activeCalendarDialog).
 *
 * Grows SchedulerDialogs into the full shell-wide dialog host the keyboard
 * conformance notes have been asking for ("moving them into SchedulerDialogs
 * … unlocks w, ü, x in one move"). Every dialog component here is the
 * EXISTING battle-tested one from the legacy tree — only the state plumbing
 * is new: one discriminated union, payload captured at request time, no
 * per-dialog flag pairs, no callbacks parked in useState.
 */
import { observer } from "mobx-react";
import React from "react";
import { AppointmentDto } from "../../api-client";
import WarningDialog from "../../atoms/WarningDialog";
import useStore from "../../helpers/useStore";
import { CancelAppointmentDialog } from "../../molecules/CancelAppointmentDialog";
import EditEventTitleDialog from "../../molecules/Calendar/EditEventTitleDialog";
import { CalendarContext } from "../../stores/Calendar/calendar.provider";
import {
  useUpdateContinuousTimeSlot,
  useUpdateTimeSlot,
} from "../../stores/Calendar/calendar.mutations";
import { useCancelContinuous } from "../../stores/Appointment/appointment.mutation";
import { useCachedAppointmentLookup } from "../../stores/Calendar/calendar.queries";
import { SetInformedDialog } from "../PatientInforming/SetInformedDialog";
import AppointmentOverviewModal from "../PatientOverview/RxInfo/AppointmentOverviewModal";
import { TimeSlotAttendanceDialog } from "../PatientOverview/RxInfo/TerminView";
import AppointmentEditCard from "../ScheduleOverview/Appointments/AppointmentEditCard";
import EditCancellationDialog from "../ScheduleOverview/Appointments/CancelAppointmentEditCard";
import TherapyMethodPickerDialog from "../ScheduleOverview/Appointments/TherapyMethodPickerDialog";

const CalendarActionDialogs: React.FC = observer(() => {
  const CalendarStore = useStore(CalendarContext);
  const dialog = CalendarStore.activeCalendarDialog;
  const close = CalendarStore.closeCalendarDialog;

  const { mutate: updateEventTimeSlot } = useUpdateTimeSlot();
  const { mutate: updateContinuousEventTimeSlot } = useUpdateContinuousTimeSlot();
  const { mutateAsync: cancelContinuousAppointment } = useCancelContinuous();
  const { lookupByAppointmentId } = useCachedAppointmentLookup();

  if (!dialog) return null;

  switch (dialog.kind) {
    case "confirm":
      return (
        <WarningDialog
          title={dialog.title}
          content={dialog.message}
          open
          onClose={close}
          onContinue={() => {
            close();
            void dialog.onConfirm();
          }}
        />
      );

    case "therapyMethod":
      // MD-302: pick a non-default therapy method for one timeslot.
      return (
        <TherapyMethodPickerDialog open timeSlotId={dialog.timeSlotId} onClose={close} />
      );

    case "notice":
      // `typeToProceed` is WarningDialog's acknowledge-only shape: ONE button that
      // just closes, instead of the Abbrechen/Bestätigen pair. A report on an
      // already-applied change has nothing to confirm or cancel.
      return (
        <WarningDialog
          title={dialog.title}
          content={dialog.message}
          open
          onClose={close}
          typeToProceed={["Ok"]}
        />
      );

    case "editEventTitle":
      return (
        <EditEventTitleDialog
          open
          initialTitle={dialog.initialTitle}
          onClose={close}
          onSave={(newTitle: string) => {
            close();
            const timeslot = {
              id: dialog.eventId,
              type: dialog.isContinuous ? "ContinuousEventTimeSlot" : "EventTimeSlot",
              title: newTitle,
            };
            if (dialog.isContinuous) {
              updateContinuousEventTimeSlot({ id: dialog.eventId, timeslot });
            } else {
              updateEventTimeSlot({ id: dialog.eventId, timeslot });
            }
          }}
        />
      );

    case "attendance":
      return (
        <TimeSlotAttendanceDialog
          appointment={dialog.appointment}
          dialogOpen
          setDialogOpen={(open: boolean) => {
            if (!open) close();
          }}
          isCalendarView
          patientId={dialog.patientId}
        />
      );

    case "cancelAppointment":
      return (
        <CancelAppointmentDialog
          patientId={dialog.patientId}
          isOpen
          onClose={close}
          appointment={dialog.appointment}
          initialSelectedTimeslotIds={dialog.initialSlotIds}
        />
      );

    case "cancelContinuous":
      return (
        <WarningDialog
          title="Stornieren"
          content="Bist du sicher, dass du den Dauertermin stornieren möchtest?"
          open
          onClose={close}
          onContinue={() => {
            close();
            // Cache invalidation lives inside the mutation; no manual refresh.
            void cancelContinuousAppointment({ appointmentId: dialog.appointmentId });
          }}
        />
      );

    case "editCancellation":
      return (
        <EditCancellationDialog
          open
          onClose={close}
          appointment={dialog.appointment}
          patientId={dialog.patientId}
        />
      );

    case "editAppointment":
      return (
        <AppointmentEditCard
          open
          appointment={dialog.appointment as AppointmentDto}
          onClose={close}
          patientId={dialog.patientId}
          isContinous={dialog.isContinuous}
        />
      );

    case "appointmentOverview":
      return (
        <AppointmentOverviewModal
          open
          onClose={close}
          patientId={dialog.patientId}
          selectedRxId={dialog.rxId}
        />
      );

    case "setInformed": {
      const slot = dialog.slot;
      // Prefer the full appointment's time slots; fall back to the clicked slot
      // alone (same derivation as the legacy wrapper).
      const appointment = slot.appointmentId
        ? lookupByAppointmentId(slot.appointmentId)
        : undefined;
      const slots = appointment?.timeSlots?.length
        ? appointment.timeSlots.map((ts) => ({
            id: ts.id,
            start: ts.start,
            end: ts.end,
            informing: ts.informing,
            rxNumber: (ts as { therapyRx?: { rx?: { rxNumber?: string } } }).therapyRx?.rx
              ?.rxNumber,
            // MD-232 D3: name what was actually delivered in the slot; the
            // prescription line only pins a variant when the doctor named one.
            therapy: (
              ts as {
                therapy?: { abbreviation?: string };
                therapyRx?: { prescribedVariant?: string | null };
              }
            ).therapy?.abbreviation ??
              (ts as { therapyRx?: { prescribedVariant?: string | null } }).therapyRx
                ?.prescribedVariant ?? undefined,
          }))
        : [
            {
              id: slot.id,
              start: slot.start,
              end: slot.end,
              informing: slot.informing,
              rxNumber: slot.rx?.rxNumber,
              therapy: slot.therapy?.abbreviation,
            },
          ];
      return (
        <SetInformedDialog
          open
          onClose={close}
          appointmentId={slot.appointmentId}
          appointmentStart={slot.start ? new Date(slot.start) : undefined}
          projection={slot.informing}
          visitType={slot.visitType}
          patientName={slot.patient?.fullName}
          slots={slots}
        />
      );
    }

    default:
      return null;
  }
});
CalendarActionDialogs.displayName = "CalendarActionDialogs";

export default CalendarActionDialogs;
